import { MenuItem } from "@mui/material";
import {
  type ExamFormDataInputAnswer,
  type ExamFormDataInputRow,
  type ExamFormTableColumnAnswer,
  type ExamFormTableRow,
} from "@rmh/defs";
import { getOptionsFromTitle } from "../../utils/examFormUtils";
import DegreesButtonSubInput from "./DegreesButtonSubInput";
import NegativePositiveSlider from "./NegativePositiveSlider";
import PercentSliderSubInput from "./PercentSliderSubInput";
import PosNegSubInput from "./PosNegSubInput";
import WflSubInput from "./WflSubInput";
import YesNoSubInput from "./YesNoSubInput";
import MultiSelectInput from "./MultiSelectInput";
import TextWithEndTagInput from "./TextWithEndTagInput";
import SingleSelectInput from "./SingleSelectInput";
import SingleBooleanInput from "./SingleBooleanInput";

interface SubInputProps {
  row: ExamFormTableRow | ExamFormDataInputRow;
  answers?: ExamFormDataInputAnswer | ExamFormTableColumnAnswer;
  subInputType?: string;
  onInputChange?: (...values: unknown[]) => void;
  disabled: boolean;
}

const SubInput = ({
  row,
  answers,
  subInputType,
  onInputChange,
  disabled,
}: SubInputProps) => {
  if (subInputType === "PosNeg") {
    const defaultValues = answers?.inputValuePosNeg ?? null;
    return (
      <PosNegSubInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "YesNo") {
    const defaultValues = answers?.inputValueYesNo ?? null;
    return (
      <YesNoSubInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "Percent") {
    const defaultValues = answers?.inputValuePercent
      ? String(answers.inputValuePercent)
      : "";
    return (
      <TextWithEndTagInput
        defaultValue={defaultValues}
        disabled={disabled}
        endText="%"
        onInputChange={onInputChange}
        type="number"
      />
    );
  }
  if (subInputType === "DegreesNumber") {
    const defaultValues = answers?.inputValueDegrees
      ? String(answers.inputValueDegrees)
      : "";
    return (
      <TextWithEndTagInput
        defaultValue={defaultValues}
        disabled={disabled}
        endText="Degrees"
        onInputChange={onInputChange}
        type="number"
      />
    );
  }
  if (subInputType === "DegreesButton") {
    const defaultValues = answers?.inputValueText ?? null;
    return (
      <DegreesButtonSubInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "PercentSlider") {
    const defaultValues = answers?.inputValuePercent ?? null;
    return (
      <PercentSliderSubInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "Temperature") {
    const defaultValues = answers?.inputValueTemp
      ? String(answers.inputValueTemp)
      : "";
    return (
      <TextWithEndTagInput
        defaultValue={defaultValues}
        disabled={disabled}
        endText="°F"
        onInputChange={onInputChange}
        type="number"
      />
    );
  }
  if (subInputType === "Checkbox") {
    const defaultValues = answers?.inputValueBoolean ?? null;
    return (
      <SingleBooleanInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "TextField") {
    const defaultValues = answers?.inputValueText ?? "";
    return (
      <TextWithEndTagInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "NumberField") {
    const defaultValues = answers?.inputValueNumber
      ? String(answers.inputValueNumber)
      : "";
    return (
      <TextWithEndTagInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
        type="number"
      />
    );
  }
  if (subInputType === "Radio") {
    const defaultValues = answers?.inputValueBoolean ?? null;
    return (
      <SingleBooleanInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
        type="radio"
      />
    );
  }
  if (subInputType === "NegativePositiveSlider") {
    const defaultValues = answers?.inputValueNumber ?? null;
    return (
      <NegativePositiveSlider
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "Wfl") {
    const defaultBoolValue = answers?.inputValueBoolean ?? true;
    const defaultTextValue = answers?.inputValueText ?? "";
    return (
      <WflSubInput
        defaultBoolValue={defaultBoolValue}
        defaultTextValue={defaultTextValue}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    );
  }
  if (subInputType === "MultipleSelectDropdown") {
    const defaultValues = answers?.inputValueMultiSelect
      ? answers.inputValueMultiSelect
      : [];
    return (
      <MultiSelectInput
        defaultValues={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      >
        {getOptionsFromTitle(row.rowTitle ?? "", row.id).map((option) => {
          return (
            <MenuItem key={option.key} value={option.value}>
              {option.label}
            </MenuItem>
          );
        })}
      </MultiSelectInput>
    );
  }
  if (subInputType === "SingleSelectDropdown") {
    const defaultValues = answers?.inputValueText ?? null;
    return (
      <SingleSelectInput
        defaultValue={defaultValues}
        disabled={disabled}
        onInputChange={onInputChange}
      >
        {getOptionsFromTitle(row.rowTitle ?? "", row.id).map((option) => {
          return (
            <MenuItem key={option.key} value={option.value}>
              {option.label}
            </MenuItem>
          );
        })}
      </SingleSelectInput>
    );
  }
  if (subInputType === "SecondsNumber") {
    const defaultValues = answers?.inputValueSeconds
      ? String(answers.inputValueSeconds)
      : "";
    return (
      <TextWithEndTagInput
        defaultValue={defaultValues}
        disabled={disabled}
        endText="Seconds"
        onInputChange={onInputChange}
        type="number"
      />
    );
  }
  if (subInputType === "RepsNumber") {
    const defaultValues = answers?.inputValueNumber
      ? String(answers.inputValueNumber)
      : "";
    return (
      <TextWithEndTagInput
        defaultValue={defaultValues}
        disabled={disabled}
        endText="Reps"
        onInputChange={onInputChange}
        type="number"
      />
    );
  }
  return null;
};

export default SubInput;
